import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import { assertIsNumber } from "common/utils/assertOfType";
import { DIST_EPSILON } from "geom/core";
import type { PlanarGraph } from "geom/planarGraph";
import {
  planarGraphAdjacentHalfEdge,
  planarGraphHalfEdgeEndVertex,
  planarGraphHalfEdgeStartVertex,
} from "geom/planarGraph";
import { polyLine2End, polyLine2Start } from "geom/polyLine2";
import { vec2Dist } from "geom/vec2";

interface TraverseSolidWallsInPlanarGraphOptions {
  readonly planarGraph: PlanarGraph;
  readonly wallMassSystem: WallMassSystem;
  readonly initialHalfEdgeIndex: number;
  readonly clockwise?: boolean; // the wall traverse system uses CCW, but this is exposed for other implementations
  readonly traverseLimit?: number;
}

interface TraverseSolidWallsInPlanarGraphResult {
  readonly vertices: number[];
  readonly edgeIndexToHalfEdgeIndex: (number | undefined)[];
  readonly halfEdges: { [halfEdgeIndex: number]: boolean };
}

/**
 * Traverse the solid walls in a planar graph starting at a given half edge index.
 */
export function traverseSolidWallsInPlanarGraph({
  planarGraph,
  wallMassSystem,
  initialHalfEdgeIndex,
  clockwise = false,
  traverseLimit = 1000,
}: TraverseSolidWallsInPlanarGraphOptions): TraverseSolidWallsInPlanarGraphResult {
  const vertices: number[] = [];
  const edgeIndexToHalfEdgeIndex: (number | undefined)[] = [];
  const halfEdges: { [halfEdgeIndex: number]: boolean } = {};

  let halfEdge = initialHalfEdgeIndex;
  let loopIndex = 0;
  let edgeIndex = 0;

  let prevx2: number | undefined = undefined;
  let prevy2: number | undefined = undefined;

  do {
    const wall = wallMassSystem.walls[wallMassSystem.halfEdgeToWall[halfEdge]];
    if (wall.wallType === WallType.SOLID_MASS) {
      const leftIsSide = wall.path.halfEdgeIndex === halfEdge;
      const layers = wall.layers;

      // for now, we default to the outermost layer
      const layer = layers[layers.length - 1];
      const { wallType } = layer;
      if (wallType === WallType.VOID) {
        /**
         * For the time being, we omit void wall layers from layer extents information. This is simply to
         * mimic legacy behavior where there were no void wall layers until a dedicated effort to adress them is in place.
         */
        console.warn("traverseSolidWalls found void wall layer, this should not happen");
        continue;
      }

      const layerInsideEdge = leftIsSide ? layer.leftEdge : layer.rightEdge;

      const [sx, sy] = polyLine2Start(layerInsideEdge);
      const [ex, ey] = polyLine2End(layerInsideEdge);

      const [x1, y1] = clockwise ? [sx, sy] : [ex, ey];
      const [x2, y2] = clockwise ? [ex, ey] : [sx, sy];

      halfEdges[halfEdge] = true;

      // zero length layer edges are ignored (often at repaired wall miters)
      if (vec2Dist(x1, y1, x2, y2) < DIST_EPSILON) {
        halfEdge = getNextSolidHalfEdge(planarGraph, wallMassSystem, halfEdge, clockwise);
        loopIndex++;
        continue;
      }

      //insert edge not associated with a half edge
      // usually a jog or end of wall jutting into a room
      if (loopIndex > 0) {
        assertIsNumber(prevx2);
        assertIsNumber(prevy2);
        if (vec2Dist(x1, y1, prevx2, prevy2) > DIST_EPSILON) {
          vertices.push(prevx2, prevy2);
          edgeIndexToHalfEdgeIndex[edgeIndex] = undefined;
          edgeIndex++;
        }
      }

      vertices.push(x1, y1);
      edgeIndexToHalfEdgeIndex[edgeIndex] = halfEdge;

      [prevx2, prevy2] = [x2, y2];

      edgeIndex++;
      loopIndex++;
      halfEdge = getNextSolidHalfEdge(planarGraph, wallMassSystem, halfEdge, clockwise);
    }
  } while (halfEdge !== initialHalfEdgeIndex && loopIndex < traverseLimit);

  // if we hit the traverse limit, we need a developer error to surface.
  if (loopIndex > traverseLimit - 2) {
    console.warn("traverseSolidWallsInPlanarGraph hit traverse limit. This should not occur.");
  }

  // last point
  assertIsNumber(prevx2);
  assertIsNumber(prevy2);
  vertices.push(prevx2, prevy2);

  return { vertices, edgeIndexToHalfEdgeIndex, halfEdges };
}

// Returns the next solid wall half edge at a planar graph vertex
function getNextSolidHalfEdge(
  planarGraph: PlanarGraph,
  wallMassSystem: WallMassSystem,
  halfEdgeIndex: number,
  clockwise: boolean,
) {
  const vertexIndex = clockwise
    ? planarGraphHalfEdgeStartVertex(planarGraph, halfEdgeIndex)
    : planarGraphHalfEdgeEndVertex(planarGraph, halfEdgeIndex);
  const curHalfEdges = planarGraph.vertexHalfEdges[vertexIndex]; //sorted CCW

  const solidWallHalfEdgesAtVertex = curHalfEdges.filter(
    (x) => wallMassSystem.walls[wallMassSystem.halfEdgeToWall[x]].wallType === WallType.SOLID_MASS,
  );

  const solidWallHalfEdgesLength = solidWallHalfEdgesAtVertex.length;
  const indexOfHalfEdge = solidWallHalfEdgesAtVertex.indexOf(
    clockwise ? halfEdgeIndex : planarGraphAdjacentHalfEdge(halfEdgeIndex),
  );

  let nextHalfEdgeIndex =
    solidWallHalfEdgesAtVertex[
      (clockwise ? indexOfHalfEdge + 1 : indexOfHalfEdge - 1 + solidWallHalfEdgesLength) % solidWallHalfEdgesLength
    ];

  if (clockwise) {
    nextHalfEdgeIndex = planarGraphAdjacentHalfEdge(nextHalfEdgeIndex);
  }
  return nextHalfEdgeIndex;
}
